import React from 'react';
import { CloudSiteData, CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import {
  CloudSiteLink,
  SoldToAddressDetailsLink,
  emptyStateSearchTabs,
  errorStateProp,
  NoData,
  PPC_TABS,
  TableHeading,
} from '@css-xp/ppc-common';
import { DynamicResultTable, FormattedAddress } from '@css-xp/base';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import './site-url-table.scss';

export interface SiteUrlTableProps {
  cloudSites: CloudSiteData[];
  transactionAccountId: string;
  isLoading: boolean;
  error?: any;
  shouldHideInnerHeading?: boolean;
  isDesignIteration1Enabled?: boolean;
}

const wrapInCell = (node?: React.ReactElement | string | null) => {
  return <div className="site-url-cell">{node}</div>;
};

const makeHead = () => {
  const cells = [
    {
      content: wrapInCell('Site'),
    },
    {
      content: wrapInCell('Company'),
    },
    {
      content: wrapInCell('Sold-to address'),
    },
  ];
  return {
    cells,
  };
};

const getSoldToAddress = (
  shipTo: CommerceAddress | undefined | null,
  transactionAccountId: string,
  isDesignIteration1Enabled?: boolean
) => {
  if (!shipTo) {
    return <NoData />;
  }
  return (
    <SoldToAddressDetailsLink
      txaId={transactionAccountId}
      shipToId={shipTo?.id}
      shouldDisableLink={isDesignIteration1Enabled}
    >
      <span className="line-clamp">
        <FormattedAddress
          addressLine1={shipTo?.addressLine1}
          addressLine2={shipTo?.addressLine2}
          city={shipTo?.city}
          country={shipTo?.country}
          postcode={shipTo?.postcode}
          state={shipTo?.state}
        />
      </span>
    </SoldToAddressDetailsLink>
  );
};

const getCompanyName = (shipTo: CommerceAddress | undefined | null, transactionAccountId: string) => {
  if (!shipTo || !shipTo.name) {
    return <NoData />;
  }
  return (
    <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={shipTo.name}>
      <span className="line-clamp">
        <FormattedAddress organizationName={shipTo.name} />
      </span>
    </SoldToAddressDetailsLink>
  );
};

const makeColumns = (
  cloudSites: CloudSiteData[],
  transactionAccountId: string,
  isDesignIteration1Enabled?: boolean
) => {
  return cloudSites.map((cloudSiteData: CloudSiteData, index) => {
    const rowKey = `site-url-row-${index}`;
    return {
      key: rowKey,
      cells: [
        {
          content: wrapInCell(
            <CloudSiteLink
              siteName={cloudSiteData.cloudSiteHostname}
              txaId={transactionAccountId}
              cloudSiteId={cloudSiteData.cloudSiteId}
            />
          ),
        },
        {
          content: wrapInCell(getCompanyName(cloudSiteData.shipToParty, transactionAccountId)),
        },
        {
          content: wrapInCell(
            getSoldToAddress(cloudSiteData.shipToParty, transactionAccountId, isDesignIteration1Enabled)
          ),
        },
      ],
    };
  });
};

export const SiteUrlTable = (siteUrlTableProps: SiteUrlTableProps) => {
  const {
    cloudSites,
    error,
    transactionAccountId,
    isLoading,
    shouldHideInnerHeading,
    isDesignIteration1Enabled,
  } = siteUrlTableProps;

  return (
    <div className="site-urls-container">
      <DynamicResultTable
        testId={'site-url'}
        beforeTable={shouldHideInnerHeading ? <></> : <TableHeading>{PPC_TABS.SITE_URLS}</TableHeading>}
        head={makeHead()}
        rows={makeColumns(cloudSites, transactionAccountId, isDesignIteration1Enabled)}
        emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateSearchTabs}
        error={error?.toString()}
        errorStateProps={errorStateProp}
        isPaginationEnabled={false}
        isLoading={isLoading}
        hideHeadOnEmptyState={isDesignIteration1Enabled}
      />
    </div>
  );
};
